import { useEffect, useState } from 'react';
import { ContactInfo } from '../Types/Types';

const apiUrl = process.env.REACT_APP_API_URL;
const baseUrl = `${apiUrl}/api/contact-info`;

const useContactInfo = () => {
  const [contactInfos, setContactInfos] = useState<ContactInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const apiUrl = process.env.REACT_APP_API_URL;
  const url = `${apiUrl}/api/contact-info`;

  const fetchContactInfos = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');
    const lang = localStorage.getItem('lang') || 'en';
    const encodedSearchQuery = encodeURIComponent(searchQuery);


    const url = `${baseUrl}?lang=${lang}&search=${encodedSearchQuery}&sortBy=${sortBy}&sortOrder=${sortOrder}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch contact info');
      }

      const data = await response.json();
      setContactInfos(data);
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching contact info');
    } finally {
      setLoading(false);
    }
  };

  const createContactInfo = async (
    name: { en: string; mk: string; sq?: string },
    description: { en: string; mk: string; sq?: string },
    imageFile?: File
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('name', JSON.stringify(name));
    formData.append('description', JSON.stringify(description));
    if (imageFile) formData.append('image', imageFile);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(baseUrl, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create contact info');
      }

      const created = await response.json();
      setContactInfos(prev => [created, ...prev]); // Optimistic update
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred while creating contact info');
      return false;
    } finally {
      setLoading(false);
    }
  };


  const updateContactInfo = async (productId: string, formData: FormData) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${url}/${productId}`, {
        method: "PUT",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to update product");
      }

      await fetchContactInfos(); // Refresh list to reflect updated product
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };


  const deleteContactInfo = async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${baseUrl}/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete contact info');
      }

      setContactInfos(prev => prev.filter(item => item._id !== id)); // Optimistic update
      return true;
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting contact info');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactInfos();
  }, [searchQuery, sortBy, sortOrder]);

  return {
    contactInfos,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    createContactInfo,
    updateContactInfo,
    deleteContactInfo,
  };
};

export default useContactInfo;